import { useRef, useState, useCallback, type MouseEvent } from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { gutters, hideScrollbarCss } from 'app/spacing';
import { DayCalendarCell, isCellToday } from 'components/Calendar';
import useEndsOfScroll from 'hooks/useEndsOfScroll';
import ScrollFadeContainer from '__components/Kizen/ScrollFadeContainer';
import { ActivityPill } from './ActivityPill';
import { ScheduleActivityButton } from './ScheduleActivityButton';
import { CALENDAR_VIEW_OPTIONS } from 'components/Calendar/constants';
import ScrollBarDetached from '__components/Layout/ScrollBarDetached';
import { useSyncSizes } from 'components/Tables/Big/hooks';
import useSyncScroll from 'react-use-sync-scroll';
import type { CalendarItem, ScheduledActivity } from '../types';
import { ExternalCalendarEventPill } from './common';

const StyledDayCell = styled(DayCalendarCell)<{
  isToday: boolean;
  skipped?: boolean;
}>`
  position: relative;
  background-color: ${({ isToday }) =>
    isToday
      ? 'var(--color-background-hover)'
      : 'var(--color-background-white)'};
  padding-bottom: ${gutters.spacing()}px;
  padding-top: ${gutters.spacing()}px;
  ${({ skipped }) =>
    skipped
      ? css`
          background-image: repeating-linear-gradient(
            -35deg,
            transparent 10px,
            var(--color-background-medium) 11px,
            transparent 12px,
            transparent 20px
          );
        `
      : ''}
`;

const EventsScrollContainerWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
`;

const EventsScrollContainer = styled(ScrollFadeContainer)`
  ${hideScrollbarCss}
  overflow: auto;
  height: 100%;
  width: calc(100% - ${gutters.spacing(1, 4)}px);
`;

const ContentForSyncHeight = styled.div`
  padding-left: ${gutters.spacing()}px;
  display: flex;
  flex-wrap: wrap;
  gap: ${gutters.spacing(1)}px;

  & > div {
    margin-right: ${gutters.spacing()}px;
  }
`;

const StyledScrollbar = styled(ScrollBarDetached)`
  margin-right: ${gutters.spacing(1, 4)}px;
`;

interface BuildDayCellProps {
  activitiesLookup: Record<string, (ScheduledActivity | CalendarItem)[]>;
  onActivityClick: (activity: ScheduledActivity) => void;
  onActivityAction: (activity: ScheduledActivity, action: string) => void;
  onAddActivity: (date: Date, time: string) => void;
  canEdit: boolean;
  timezone: string;
  businessTimezone: string;
  skipLookup?: Record<string, boolean>;
  skipWeekdays?: number[];
  isMobile?: boolean;
}

export const buildDayCell =
  ({
    activitiesLookup,
    onActivityClick,
    onActivityAction,
    onAddActivity,
    canEdit,
    timezone,
    businessTimezone,
    skipLookup = {},
    skipWeekdays = [],
    isMobile,
  }: BuildDayCellProps) =>
  ({ date, time, columnIndex, totalColumns, ...rest }: any) => {
    const ref = useRef(null);
    const [showAddButton, setShowAddButton] = useState(false);

    // Create lookup key from date and time
    const dateKey = `${date.toDateString()}-${time}`;
    const activities = activitiesLookup[dateKey] || [];

    // Determine if this date should be marked as skipped (non-working day)
    const skipped = skipLookup[date.toDateString()];
    const skippedByWeekday = skipWeekdays.includes(date.getDay());
    // Show overlay if: explicitly skipped OR (not explicitly unskipped AND is a skip weekday)
    const isSkipped =
      skipped === true || (skipped !== false && skippedByWeekday);

    const handleAddClick = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        onAddActivity(date, time);
      },
      [date, time]
    );

    const handleMouseEnter = useCallback(() => {
      setShowAddButton(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setShowAddButton(false);
    }, []);

    const [refFn, scrollRef] = useSyncSizes(
      ref,
      '.ContentForSyncHeight',
      'height',
      [activities],
      false
    );
    const scrolled = useEndsOfScroll(scrollRef, [activities]);
    useSyncScroll(useRef([scrollRef, ref]), {
      vertical: true,
    });

    return (
      <StyledDayCell
        date={date}
        isToday={isCellToday(date)}
        skipped={isSkipped}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {canEdit ? (
          <ScheduleActivityButton
            view={CALENDAR_VIEW_OPTIONS.day}
            show={showAddButton}
            onClick={handleAddClick}
          />
        ) : null}
        <EventsScrollContainerWrapper>
          <EventsScrollContainer ref={refFn} scrolled={scrolled}>
            <ContentForSyncHeight className="ContentForSyncHeight">
              {activities.map((activity) => {
                if (activity.isCalendarEvent) {
                  return (
                    <ExternalCalendarEventPill
                      key={activity.id}
                      id={activity.id}
                      date={activity.date}
                      isSpanning={activity.isSpanning}
                      customColor="var(--color-viz-cat-1-primary)"
                      isPastDate={activity.dueDatetime < Date.now()}
                      url={activity.url}
                      tooltipPosition="top"
                      event={activity}
                    >
                      {activity.name}
                    </ExternalCalendarEventPill>
                  );
                }

                return (
                  <ActivityPill
                    key={activity.id}
                    activity={activity}
                    timezone={timezone}
                    businessTimezone={businessTimezone}
                    onComplete={onActivityClick}
                    onEdit={(act) => onActivityAction(act, 'edit')}
                    onDelete={(id) =>
                      onActivityAction(
                        activities.find(
                          (a) => a.id === id
                        ) as ScheduledActivity,
                        'delete'
                      )
                    }
                    maxWidth="124px"
                    canEdit={canEdit}
                    columnIndex={columnIndex}
                    totalColumns={totalColumns}
                    tooltipPosition="top"
                    isMobile={isMobile}
                    // Disable the hover effect on the pill since we do it with Javascript in order to highlight all pills with the same activity id across cells
                    disableHoverEffect
                  />
                );
              })}
            </ContentForSyncHeight>
          </EventsScrollContainer>
          <StyledScrollbar ref={ref} scrollClassName="ContentForSyncHeight" />
        </EventsScrollContainerWrapper>
      </StyledDayCell>
    );
  };
